import React from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import { COLORS, COLORS_DARK } from '../../constants';
import useResponsiveChart from '../../hooks/useResponsiveChart';
import CustomTooltip from '../CustomTooltip';
import CustomLegend from '../CustomLegend';
import ExplanationCard from '../ExplanationCard';

const AreaChartView = ({ selectedMethod, selectedModel, chartData, selectedLanguages }) => {
  const { isDarkMode } = useTheme();
  const colors = isDarkMode ? COLORS_DARK : COLORS;
  const { chartHeight, chartMargin, axisLabelFontSize, tickFontSize } = useResponsiveChart();

  // Convert chartData format to match original (documents -> docCount)
  const areaData = chartData.map(item => ({
    docCount: item.documents,
    ...Object.fromEntries(
      selectedLanguages.map(lang => [lang, item[lang]])
    )
  }));

  return (
    <div className="mb-8">
      <ExplanationCard title="Area Chart Analysis Explanation">
        <p><strong>Area Chart:</strong> Shows cumulative performance trends with filled areas under each line.</p>
        <p><strong>Filled Areas:</strong> Each language's performance is represented by a colored area.</p>
        {/* <p><strong>Layered View:</strong> Areas are stacked to show both individual and cumulative performance across languages.</p> */}
        {/* <p><strong>Interpretation:</strong> Larger filled areas indicate better sustained performance across document counts.</p> */}
      </ExplanationCard>

      <h3 className="text-3xl font-bold mb-3 text-center" style={{ color: 'var(--text-primary)' }}>
        Performance Area Analysis
      </h3>
      <p className="text-lg text-center mb-8" style={{ color: 'var(--text-secondary)' }}>
        Area chart visualization for {selectedMethod} method - {selectedModel} model.
      </p>

      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height={chartHeight}>
          <AreaChart data={areaData} margin={chartMargin}>
            <CartesianGrid strokeDasharray="4 4" stroke={isDarkMode ? '#333333' : '#e0e7ff'} />
            <XAxis
              dataKey="docCount"
              label={{ value: 'Number of Documents', position: 'bottom', offset: 40, fill: isDarkMode ? '#a3a3a3' : '#4a5568', fontSize: axisLabelFontSize, fontWeight: 'bold' }}
              tickLine={false}
              axisLine={{ stroke: isDarkMode ? '#404040' : '#cbd5e0' }}
              padding={{ left: 15, right: 15 }}
              interval="preserveStartEnd"
              tick={{ fill: isDarkMode ? '#a3a3a3' : '#4a5568', fontSize: tickFontSize }}
            />
            <YAxis
              label={{ value: 'Performance Score', angle: -90, position: 'insideLeft', offset: 10, fill: isDarkMode ? '#a3a3a3' : '#4a5568', fontSize: axisLabelFontSize, fontWeight: 'bold' }}
              tickLine={false}
              axisLine={{ stroke: isDarkMode ? '#404040' : '#cbd5e0' }}
              domain={['dataMin - 10', 'dataMax + 10']}
              tick={{ fill: isDarkMode ? '#a3a3a3' : '#4a5568', fontSize: tickFontSize }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<CustomLegend />} wrapperStyle={{ paddingTop: '20px' }} />
            {selectedLanguages.map(lang => (
              <Area
                key={lang}
                type="monotone"
                dataKey={lang}
                stroke={colors[lang]}
                fill={colors[lang]}
                fillOpacity={0.3}
                strokeWidth={2}
                name={lang}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AreaChartView;